import * as _ from "lodash";
import * as $ from "jquery";

import { ExtensionBase } from "../extensionBase";
import { ModelApi } from "../../api/modelApi";
import { viewerOptions, viewerState } from "../../viewer";
import * as SmartDrawings from "../../smartDrawings";
import { UiUtils } from "../../common/uiUtils";
import { ViewerUtils } from "../../common/viewerUtils";

const roleOverrides = {
    "review": { displayName: "Review", name: "review" },
    "minimal": { displayName: "Review", name: "minimal" },
    "full": { displayName: "Full", name: "full" },
}

/**
 * This extension fetches the roles, so it should ALWAYS be loaded. Otherwise the roles (even if there is just one) won't work.
 * Creates a menu from all the available roles for switching between them. 
 */
export class RoleExtension extends ExtensionBase {

    static extensionName = "RoleExtension";

    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private roleButton: any; //Autodesk.Viewing.UI.ComboButton;

    private roles: SmartDrawings.ModelRole[];
    private tools = {};

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
        this.onModelRootLoaded = this.onModelRootLoaded.bind(this);
    }

    static registerExtension() {
        super.registerExtension(RoleExtension.extensionName, RoleExtension);
    }

    async onToolbarCreated() {
        this.subToolbar = this.getControlGroup("SmartDrawings.RoleExtensions");
        await this.initRoles();
        this.createUi();
    }

    private createUi() {
        if (this.roles.length <= 1) {
            return;
        }

        if (this.roleButton != null) {
            this.subToolbar.removeControl(this.roleButton);
        }

        this.roleButton = UiUtils.createComboButton({
            id: "SmartDrawings.RoleButton",
            tooltip: "Role",
            iconClass: "ion-md-person",
        });
        this.roleButton.setToolTip(`Role: ${viewerState.currentRole.displayName}`);
        for (let role of this.roles) {
            const menuButton: any = UiUtils.createButton({
                id: `roleMenuButton_${role.name}`,
                tooltip: role.displayName,
                onClick: () => { this.chooseRole(viewerState.currentRole, role)}
            });
            menuButton.container.innerHTML = `<div style="width: max-content">${_.escape(role.displayName)}</div>`;
            menuButton.addClass("toolbar-text-button");
            this.roleButton.addControl(menuButton);
        }

        this.subToolbar.addControl(this.roleButton);
    }

    /**
     * Fetches the available roles and tries to set the initial role by a default value.
     */
    private async initRoles() {
        let defaultRole: SmartDrawings.ModelRole;

        const roleOverride = roleOverrides[viewerOptions.role];
        if (roleOverride != null) {
            this.roles = [roleOverride];
            defaultRole = roleOverride;
        } else {
            let data;
            if (viewerOptions.comboModelId)
                ({ data } = await ModelApi.getComboModelRoles(viewerOptions.comboModelId));
            else
                ({ data } = await ModelApi.getModelRoles(viewerOptions.urn || viewerOptions.guid));

            this.roles = data.roles;
            defaultRole = _.find(data.roles, r => r.name === data.defaultRole);
        }

        const initialRole = this.getInitialRole(this.roles, defaultRole);
        this.viewer.dispatchEvent({ type: SmartDrawings.SdEvents.SD_ROLE_CHANGE, role: initialRole });
        setTimeout(() =>
            this.setTools(null, initialRole),
            300);

        setTimeout(()=> { this.setLayers(initialRole); }, 2000);
    }

    /**
     * Sets correct tools, stores the chosen role in local storage and broadcasts it in an event.
     */
    private async chooseRole(oldRole: SmartDrawings.ModelRole, newRole: SmartDrawings.ModelRole) {
        this.setTools(oldRole, newRole);
        viewerState.currentRole = newRole;
        try {
            // This will fail in sandboxed iframes.
            localStorage.setItem("SmartDrawings.Role", newRole.name);
        } catch (e) {
        }

        this.viewer.dispatchEvent({ type: SmartDrawings.SdEvents.SD_ROLE_CHANGE, role: newRole });
        this.roleButton.setToolTip(`Role: ${newRole.displayName}`);
        UiUtils.notifySuccess(`Role changed to ${newRole.displayName}`);
    }

    private getInitialRole(roles: SmartDrawings.ModelRole[], defaultRole: SmartDrawings.ModelRole): SmartDrawings.ModelRole {
        let storedName = null;
        try {
            // This will fail in sandboxed iframes.
            storedName = localStorage.getItem("SmartDrawings.Role");
        } catch (e) {
        }
        if (storedName != null) {
            const storedRole = _.find(roles, role => role.name === storedName);
            viewerState.currentRole = storedRole || defaultRole;
        } else {
            viewerState.currentRole = defaultRole;
        }
        return viewerState.currentRole;
    }

    /*
     * Toggling the tools contains a lot of magic since it's really hard to tell when and how the Forge viewer shows/hides some of its controls.
     * So all the stuff below has been defined through trial and error.
     */
    private async toggleTools(role, off) {
        // This was just used for the Fira POC, so hide by default.
        UiUtils.hideUiElement("SmartDrawings.MeasurementDataButton");

        if (this.tools[role.name] == null) {
            const { data } = await ModelApi.getModelRoleTools(role.name);
            this.tools[role.name] = data;
        }

        if (!_.isEmpty(this.tools[role.name].visible)) {
            UiUtils.hideUiElementByClass("adsk-control.adsk-control-group");
            UiUtils.hideUiElementByIdPrefix("SmartDrawings");
            UiUtils.showUiElementByIdPrefix("SmartDrawings-Panel");
            UiUtils.hideUiElementByIdPrefix("toolbar-");
            UiUtils.showUiElementByClass("adsk-control-tooltip");
            UiUtils.showUiElementByClass("toolbar-vertical-group");
            UiUtils.showUiElementByClass("adsk-hidden");
            UiUtils.showUiElementByClass("adsk-button-arrow");
        } else if (!_.isEmpty(this.tools[role.name].hidden)) {
            UiUtils.showUiElementByClass("adsk-control.adsk-control-group");
            UiUtils.showUiElementByIdPrefix("SmartDrawings-Panel");
            UiUtils.showUiElementByIdPrefix("toolbar-");
            UiUtils.hideUiElementByClass("smartdrawings-toolbar-div");
            UiUtils.hideUiElementByClass("smartdrawings-bcf-toolbar-div");
        } else {
            UiUtils.showUiElementByIdPrefix("toolbar-");
        }

        const hidden = this.tools[role.name][off ? "visible" : "hidden"];
        hidden.map(t => UiUtils.hideUiElement(t));

        const visible = this.tools[role.name][off ? "hidden" : "visible"];
        visible.map(t => UiUtils.showUiElement(t));
    }

    private async setTools(oldRole: SmartDrawings.ModelRole, newRole: SmartDrawings.ModelRole) {

        if (oldRole != null) {
            await this.toggleTools(oldRole, true);
        }

        await this.toggleTools(newRole, false);
        $(".adsk-toolbar").css({ cssText: "display: block !important" });
    }

    private setLayers(newRole: SmartDrawings.ModelRole) {
        const ui = newRole.uiSettings;
        if (ui != null && !_.isEmpty(ui.hiddenLayers)) {
            for (let layer of ui.hiddenLayers) {
                ViewerUtils.setLayerVisibility(layer, false, this.viewer);
            }
        }
    }

    load(): boolean {
        console.log("RoleExtension is loaded!");
        return super.load();
    };

    unload(): boolean {
        this.viewer.getToolbar(false).removeControl(this.subToolbar);
        return super.unload();
    };
}